import { Info } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FIELD_HELP } from "@/lib/fieldHelp";

/**
 * Wraps an input label with a hover/tap tooltip showing detailed help.
 *
 * Usage:
 *   <HoverTip id="annual_contribution" label="Annual contribution" />
 *
 * The label always renders (with eyebrow styling). If `id` is in FIELD_HELP,
 * a subtle info icon appears and a styled tooltip opens on hover or tap.
 */
export default function HoverTip({
  id,
  label,
  htmlFor,
  className = "",
  inline = false,
}) {
  const help = FIELD_HELP[id];
  const TagName = htmlFor ? "label" : "span";

  if (!help) {
    return (
      <TagName htmlFor={htmlFor} className={`${inline ? "" : "label-eyebrow"} ${className}`}>
        {label}
      </TagName>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <TagName
            htmlFor={htmlFor}
            data-testid={`hover-tip-${id}`}
            className={`inline-flex items-center gap-1.5 cursor-help touch-manipulation ${inline ? "" : "label-eyebrow"} ${className}`}
          >
            {label}
            <Info size={12} weight="duotone" className="opacity-50 group-hover:opacity-100 transition-opacity" />
          </TagName>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={6}
          className="z-50 max-w-[300px] bg-brand-forest border border-brand-forest text-brand-bone rounded-md p-4 shadow-xl"
        >
          <div className="space-y-2 text-left">
            <div className="font-serif text-base font-semibold text-brand-bone leading-tight">
              {help.title}
            </div>
            <p className="text-xs text-brand-bone/85 leading-relaxed">
              {help.description}
            </p>
            {help.guidance && (
              <p className="text-xs text-brand-bone/70 leading-relaxed italic">
                {help.guidance}
              </p>
            )}
            {help.example && (
              <div className="pt-2 mt-1 border-t border-brand-bone/15">
                <span className="text-[9px] uppercase tracking-[0.18em] text-brand-copper font-semibold">
                  Example
                </span>
                <p className="mt-1 text-xs text-brand-bone/90 leading-relaxed">
                  {help.example}
                </p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
